'use client';

import { c as _c } from "react/compiler-runtime";
import { MULTIPLE_COLORS } from '@/constants/ui.constants';
import type { TimeRangeConfig } from '@/api/generated/model';
import type { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { SpendHorizontalBars, SpendHorizontalBarsSkeleton } from '@/domains/pages/ai-spend/components/spend-horizontal-bars';
import { getTeamCostPerTaskBarColor } from '@/domains/pages/ai-spend/components/team-cost-per-task-bars';
import { SPEND_GROUP_EMPTY_TITLE } from '@/domains/pages/ai-spend/constants';
import { useAiSpendDistributionData } from '@/domains/pages/ai-spend/hooks/use-ai-spend-distribution-data';
import { computeTopQuartileThreshold } from '@/domains/pages/ai-spend/utils/spend-quartiles';
import { getPageTimeRangePhrase } from '@/domains/pages/shared/page-time-range';
import { PagesResizableSheet } from '@/domains/pages/shared/pages-resizable-sheet';
import { SEGMENT_ONLY_SCOPE_OPTIONS, SegmentScopeTabs, type SegmentScopeView } from '@/domains/pages/shared/segment-scope-tabs';
import { segmentKindLabel, type SegmentKind } from '@/domains/pages/shared/segment-kind';
import { PAGE_DATA_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import { formatSpend } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { useMemo, useState } from 'react';
export type SpendBreakdownMetric = 'total' | 'perTask';
type SpendBreakdownDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metric: SpendBreakdownMetric;
  orgTotalCost: number | null;
  orgSpendPerTask: number | null;
  timeRange: TimeRangeConfig | undefined;
  projectId: string | undefined;
  filters: GenericFilter[];
  specialFilters: GenericFilter[];
};
function parseSegmentKind(view: SegmentScopeView): SegmentKind {
  if (view === 'market' || view === 'tenure') return view;
  return 'team';
}
export function SpendBreakdownDrawer(t0) {
  const $ = _c(68);
  const {
    open,
    onOpenChange,
    metric,
    orgTotalCost,
    orgSpendPerTask,
    timeRange,
    projectId,
    filters,
    specialFilters
  } = t0;
  const [kind, setKind] = useState("team");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = view => {
      if (view === "all") {
        return;
      }
      setKind(parseSegmentKind(view));
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleKindChange = t1;
  let t2;
  if ($[1] !== filters || $[2] !== kind || $[3] !== open || $[4] !== orgTotalCost || $[5] !== projectId || $[6] !== specialFilters || $[7] !== timeRange) {
    t2 = {
      projectId,
      timeRange,
      filters,
      specialFilters,
      kind,
      totalCost: orgTotalCost,
      enabled: open
    };
    $[1] = filters;
    $[2] = kind;
    $[3] = open;
    $[4] = orgTotalCost;
    $[5] = projectId;
    $[6] = specialFilters;
    $[7] = timeRange;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const distribution = useAiSpendDistributionData(t2);
  let t3;
  if ($[9] !== kind) {
    t3 = segmentKindLabel(kind);
    $[9] = kind;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const segmentNoun = t3;
  const title = metric === "total" ? `Total AI spend by ${segmentNoun}` : `AI spend per task by ${segmentNoun}`;
  let t4;
  if ($[11] !== metric || $[12] !== orgSpendPerTask || $[13] !== orgTotalCost) {
    t4 = metric === "total" ? formatSpend(orgTotalCost) : formatSpend(orgSpendPerTask);
    $[11] = metric;
    $[12] = orgSpendPerTask;
    $[13] = orgTotalCost;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const summaryValue = t4;
  let t5;
  if ($[15] !== metric || $[16] !== timeRange) {
    t5 = metric === "total" ? `AI spend ${getPageTimeRangePhrase(timeRange)}` : "organization average";
    $[15] = metric;
    $[16] = timeRange;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const summaryLabel = t5;
  let t6;
  if ($[18] !== distribution.rows) {
    t6 = distribution.rows.map(_temp).filter(_temp2);
    $[18] = distribution.rows;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const perTaskValues = t6;
  let t7;
  if ($[20] !== perTaskValues) {
    t7 = computeTopQuartileThreshold(perTaskValues);
    $[20] = perTaskValues;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const topQuartileThreshold = t7;
  let t8;
  bb0: {
    if (metric === "total") {
      let t9;
      if ($[22] !== distribution.rows) {
        t9 = distribution.rows.map(_temp3);
        $[22] = distribution.rows;
        $[23] = t9;
      } else {
        t9 = $[23];
      }
      t8 = t9;
      break bb0;
    }
    let t9;
    if ($[24] !== distribution.rows || $[25] !== topQuartileThreshold) {
      let t10;
      if ($[27] !== topQuartileThreshold) {
        t10 = row_2 => ({
          key: row_2.key,
          label: row_2.name,
          value: row_2.spendPerTask,
          color: getTeamCostPerTaskBarColor(row_2.spendPerTask, topQuartileThreshold)
        });
        $[27] = topQuartileThreshold;
        $[28] = t10;
      } else {
        t10 = $[28];
      }
      t9 = [...distribution.rows].filter(_temp4).sort(_temp5).map(t10);
      $[24] = distribution.rows;
      $[25] = topQuartileThreshold;
      $[26] = t9;
    } else {
      t9 = $[26];
    }
    t8 = t9;
  }
  const bars = t8;
  let t9;
  bb1: {
    if (metric !== "total") {
      t9 = null;
      break bb1;
    }
    let t10;
    if ($[29] !== timeRange) {
      t10 = getPageTimeRangePhrase(timeRange);
      $[29] = timeRange;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    const t11 = `Total AI spend ${t10}`;
    let t12;
    if ($[31] !== bars) {
      t12 = bars.reduce(_temp6, 0);
      $[31] = bars;
      $[32] = t12;
    } else {
      t12 = $[32];
    }
    let t13;
    if ($[33] !== t11 || $[34] !== t12) {
      t13 = {
        label: t11,
        value: t12
      };
      $[33] = t11;
      $[34] = t12;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    t9 = t13;
  }
  const total = t9;
  let t10;
  if ($[36] !== title) {
    t10 = <h2 className="min-w-0 truncate text-base leading-tight font-semibold text-foreground">{title}</h2>;
    $[36] = title;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("shrink-0 text-right text-xs text-muted-foreground", PAGE_DATA_NUMBER_CLASS);
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== summaryValue) {
    t12 = <span className="text-base font-medium text-foreground">{summaryValue}</span>;
    $[39] = summaryValue;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== summaryLabel || $[42] !== t12) {
    t13 = <p className={t11}>{t12} {summaryLabel}</p>;
    $[41] = summaryLabel;
    $[42] = t12;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== t10 || $[45] !== t13) {
    t14 = <div className="flex shrink-0 items-baseline justify-between gap-4 px-5 pt-4">{t10}{t13}</div>;
    $[44] = t10;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  let t15;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="mt-4 shrink-0 border-b border-border" />;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== kind) {
    t16 = <SegmentScopeTabs value={kind} onValueChange={handleKindChange} options={SEGMENT_ONLY_SCOPE_OPTIONS} />;
    $[48] = kind;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== metric) {
    t17 = metric === "perTask" ? <div className="flex items-center gap-4 text-xs text-muted-foreground"><span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-sm" style={{
          backgroundColor: MULTIPLE_COLORS[3]
        }} />Top quartile</span><span className="inline-flex items-center gap-1.5"><span className="size-2 rounded-sm" style={{
          backgroundColor: MULTIPLE_COLORS[2]
        }} />Rest</span></div> : null;
    $[50] = metric;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t16 || $[53] !== t17) {
    t18 = <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-5 py-3">{t16}{t17}</div>;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== bars || $[56] !== distribution.isLoading || $[57] !== kind || $[58] !== total) {
    t19 = <div className="relative min-h-0 flex-1 overflow-y-auto">{distribution.isLoading ? <SpendHorizontalBarsSkeleton /> : <SpendHorizontalBars bars={bars} formatValue={formatSpend} emptyMessage={SPEND_GROUP_EMPTY_TITLE[kind]} total={total} />}</div>;
    $[55] = bars;
    $[56] = distribution.isLoading;
    $[57] = kind;
    $[58] = total;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  let t20;
  if ($[60] !== t14 || $[61] !== t18 || $[62] !== t19) {
    t20 = <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{t14}{t15}{t18}{t19}</div>;
    $[60] = t14;
    $[61] = t18;
    $[62] = t19;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== onOpenChange || $[65] !== open || $[66] !== t20) {
    t21 = <PagesResizableSheet open={open} onOpenChange={onOpenChange}>{t20}</PagesResizableSheet>;
    $[64] = onOpenChange;
    $[65] = open;
    $[66] = t20;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  return t21;
}
function _temp6(sum, bar) {
  return sum + bar.value;
}
function _temp5(left, right) {
  return right.spendPerTask - left.spendPerTask;
}
function _temp4(row_1) {
  return row_1.spendPerTask != null;
}
function _temp3(row_0) {
  return {
    key: row_0.key,
    label: row_0.name,
    value: row_0.cost,
    color: row_0.color
  };
}
function _temp2(value) {
  return value != null && Number.isFinite(value);
}
function _temp(row) {
  return row.spendPerTask;
}